import React, { useEffect, useRef, useState } from "react";
import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/24/outline";
import CustomDropdown from "../../dropdown/CustomDropdown";
import CustomPaginationDropdown from "../../dropdown/CustomPaginationDropDown";

const Pagination = ({
  currentPage,
  totalPages,
  nextPage,
  previousPage,
  directIntoPage,
  perPageItem,
  showNumberOfPages,
  meta,
}) => {
  const { from, to, total } = meta ?? {};
  const [activeButton, setActiveButton] = useState(1);
  const [leftArrowActive, setLeftArrowActive] = useState(false);
  const [rightArrowActive, setRightArrowActive] = useState(false);
  const [selectedOption, setSelectedOptions] = useState(perPageItem);
  const initialRender = useRef(false);

  useEffect(() => {
    setSelectedOptions(perPageItem);
  }, [perPageItem]);

  useEffect(() => {
    if (selectedOption != perPageItem) {
      nextPage(null, selectedOption);
    }
  }, [selectedOption]);

  useEffect(() => {
    if (currentPage == 1) {
      setLeftArrowActive(false);
    } else {
      setLeftArrowActive(true);
    }
    if (currentPage === totalPages) {
      setRightArrowActive(false);
    } else {
      setRightArrowActive(true);
    }
  }, [currentPage, totalPages]);

  const renderPageNumbers = (pagesToShow) => {
    const pageNumbers = [];
    const halfPagesToShow = Math.floor(pagesToShow / 2);
    let startPage = Math.max(currentPage - halfPagesToShow, 1);
    const endPage = Math.min(startPage + pagesToShow - 1, totalPages);

    // Adjust startPage if we reached the end boundary
    startPage = Math.max(endPage - pagesToShow + 1, 1);

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <li key={"item" + i}>
          <button
            className={`w-9 h-9 rounded-[10px] border border-[#EBEFF3] bg-white text-sm font-black leading-[160%] text-[#485868] flex items-center justify-center ${
              i === currentPage ? "border-primary text-primary" : ""
            }`}
            onClick={() =>
              currentPage !== i && directIntoPage(i, selectedOption)
            }
          >
            {i}
          </button>
        </li>
      );
    }
    return pageNumbers;
  };

  const handlePreviousPage = () => {
    setLeftArrowActive(true);
    setRightArrowActive(false);
    setActiveButton(currentPage - 1);
    previousPage(currentPage - 1, selectedOption);
  };

  const handleNextPage = () => {
    setRightArrowActive(true);
    setLeftArrowActive(false);
    setActiveButton(currentPage + 1);
    nextPage(currentPage + 1, selectedOption);
  };

  return (
    <>
      <div className="mt-5 flex items-center gap-2.5 justify-center">
        <button
          className={`w-9 h-9 rounded-[10px] border border-[#EBEFF3] ${
            leftArrowActive
              ? "bg-white cursor-pointer"
              : "bg-[#F1F4F6] cursor-not-allowed"
          } text-sm font-black leading-[160%] text-[#485868] flex items-center justify-center`}
          onClick={handlePreviousPage}
          disabled={!leftArrowActive}
        >
          <ArrowLeftIcon className="w-6 h-6" />
        </button>
        <ul className="flex items-center gap-2.5">
          {renderPageNumbers(showNumberOfPages)}
        </ul>
        <button
          className={`w-9 h-9 rounded-[10px] border border-[#EBEFF3] ${
            rightArrowActive
              ? "bg-white cursor-pointer"
              : "bg-[#F1F4F6] cursor-not-allowed"
          } text-sm font-black leading-[160%] text-[#485868] flex items-center justify-center`}
          onClick={handleNextPage}
          disabled={!rightArrowActive}
        >
          <ArrowRightIcon className="w-6 h-6" />
        </button>
      </div>
      <div className="mt-5 flex items-center gap-2.5 justify-center">
        <span className="text-sm font-normal leading-[160%] text-customGray">
          Displaying{" "}
          <b>
            {from ?? "--"}-{to ?? "--"}
          </b>{" "}
          of <b>{total}</b>
        </span>
        <div className="flex items-center gap-2 max-w-[144px] w-full">
          <CustomPaginationDropdown
            options={[10, 25, 50, 100]}
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOptions}
            classes="px-[14px] md:p-2 border border-[#BEC2C5] rounded-md h-9 bg-white flex items-center justify-between w-[80px]"
            textClasses="inline-flex w-full items-center text-left gap-x-[10px] text-sm lg:text-base font-medium text-customGray"
            rightIconClasses="-mr-1 h-6 w-6 text-customGray font-bold"
            menuClasses="absolute left-0 z-10 bottom-9 w-full origin-top rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
          />
          <span className="text-sm font-normal leading-[160%] text-customGray">
            Per Page
          </span>
        </div>
      </div>
    </>
  );
};

export default Pagination;
